<template>
  <div class="service-mode-dialog">
    <div class="dialog-card">
      <h2 class="dialog-title">选择服务模式</h2>
      <p class="dialog-desc">请选择 AIGatewayX 的运行模式</p>

      <div class="mode-cards">
        <div
          class="mode-card"
          :class="{ active: selectedMode === 'local', disabled: isSwitching }"
          @click="!isSwitching && (selectedMode = 'local')"
        >
          <el-icon size="32"><Monitor /></el-icon>
          <h3>本地模式</h3>
          <p>启动本地后端服务</p>
          <span class="mode-addr">http://localhost:{{ localPort }}</span>
        </div>

        <div
          class="mode-card"
          :class="{ active: selectedMode === 'remote', disabled: isSwitching }"
          @click="!isSwitching && (selectedMode = 'remote')"
        >
          <el-icon size="32"><Connection /></el-icon>
          <h3>远程模式</h3>
          <p>连接远程服务器</p>
          <span class="mode-hint">本地服务将不会启动</span>
        </div>
      </div>

      <div v-if="selectedMode === 'remote'" class="remote-config">
        <el-form :model="formData" :rules="formRules" ref="formRef" label-position="top">
          <el-form-item label="远程服务地址" prop="remoteAddress">
            <el-input
              v-model="formData.remoteAddress"
              placeholder="https://your-server.com"
              clearable
            />
          </el-form-item>
        </el-form>
      </div>

      <div v-if="selectedMode === 'local'" class="local-config">
        <el-form :model="formData" label-position="top">
          <el-form-item label="本地端口">
            <el-input-number v-model="formData.port" :min="1024" :max="65535" :step="1" />
          </el-form-item>
        </el-form>
      </div>

      <div v-if="sidecarError" class="error-block">
        <el-alert :title="sidecarError" type="error" show-icon :closable="false" />
        <el-button size="small" type="warning" @click="retryStart" style="margin-top: 8px">
          重试启动
        </el-button>
        <el-button size="small" @click="switchToRemoteMode" style="margin-top: 8px">
          切换远程模式
        </el-button>
      </div>

      <div class="dialog-footer">
        <el-button
          type="primary"
          size="large"
          :disabled="!canConfirm"
          :loading="isStarting"
          @click="handleConfirm"
          style="width: 100%"
        >
          {{ confirmText }}
        </el-button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, reactive } from 'vue';
import { Monitor, Connection } from '@element-plus/icons-vue';
import { useServiceModeStore } from '@/store/service-mode';
import type { FormInstance, FormRules } from 'element-plus';

const emit = defineEmits<{ confirmed: [] }>();

const store = useServiceModeStore();

const selectedMode = ref<'local' | 'remote'>(store.mode);
const isStarting = ref(false);
const formRef = ref<FormInstance>();

const formData = reactive({
  remoteAddress: store.serverAddress || 'https://',
  port: store.localPort || 3000,
});

const localPort = computed(() => formData.port);
const sidecarError = computed(() => store.sidecarError);
const isSwitching = computed(() => store.isSwitching);

const formRules: FormRules = {
  remoteAddress: [
    { required: true, message: '请输入远程服务地址', trigger: 'blur' },
    {
      pattern: /^https?:\/\/.+/,
      message: '请输入有效的 URL（以 http:// 或 https:// 开头）',
      trigger: 'blur',
    },
  ],
};

const canConfirm = computed(() => {
  if (isStarting.value || isSwitching.value) return false;
  if (selectedMode.value === 'remote') {
    return /^https?:\/\/.+/.test(formData.remoteAddress);
  }
  return true;
});

const confirmText = computed(() => {
  if (isStarting.value) return '正在启动本地服务...';
  if (selectedMode.value === 'local') return '启动本地服务';
  return '连接远程服务';
});

async function handleConfirm() {
  if (selectedMode.value === 'remote' && formRef.value) {
    const valid = await formRef.value.validate().catch(() => false);
    if (!valid) return;
  }

  isStarting.value = true;
  try {
    if (selectedMode.value === 'local') {
      store.localPort = formData.port;
      const ok = await store.confirmLocalMode();
      if (!ok) return; // Stay on dialog to show error / allow retry
    } else {
      store.serverAddress = formData.remoteAddress.replace(/\/+$/, '');
      await store.confirmRemoteMode();
    }
    emit('confirmed');
  } finally {
    isStarting.value = false;
  }
}

async function retryStart() {
  isStarting.value = true;
  try {
    const ok = await store.confirmLocalMode();
    if (ok) emit('confirmed');
  } finally {
    isStarting.value = false;
  }
}

function switchToRemoteMode() {
  selectedMode.value = 'remote';
}
</script>

<style scoped>
.service-mode-dialog {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: var(--el-bg-color-page, #f5f7fa);
  padding: 20px;
}

.dialog-card {
  background: var(--el-bg-color, #fff);
  border-radius: 12px;
  padding: 32px;
  max-width: 520px;
  width: 100%;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
}

.dialog-title {
  text-align: center;
  margin: 0 0 8px;
  font-size: 22px;
  font-weight: 600;
}

.dialog-desc {
  text-align: center;
  color: var(--el-text-color-secondary, #909399);
  margin: 0 0 24px;
}

.mode-cards {
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
}

.mode-card {
  flex: 1;
  border: 2px solid var(--el-border-color, #dcdfe6);
  border-radius: 10px;
  padding: 20px 16px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
}

.mode-card:hover:not(.disabled) {
  border-color: var(--el-color-primary-light-3, #79bbff);
}

.mode-card.active {
  border-color: var(--el-color-primary, #409eff);
  background: var(--el-color-primary-light-9, #ecf5ff);
}

.mode-card.disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.mode-card h3 {
  margin: 8px 0 4px;
  font-size: 16px;
}

.mode-card p {
  margin: 0 0 8px;
  font-size: 13px;
  color: var(--el-text-color-secondary, #909399);
}

.mode-addr {
  font-size: 12px;
  color: var(--el-text-color-regular, #606266);
  font-family: monospace;
}

.mode-hint {
  font-size: 12px;
  color: var(--el-color-warning, #e6a23c);
}

.remote-config, .local-config {
  margin-bottom: 16px;
}

.error-block {
  margin-bottom: 16px;
}

.dialog-footer {
  margin-top: 8px;
}
</style>
